import React, { useState } from "react";
import type { User } from "firebase/auth";
import { EmailPreferences as Preferences, saveEmailPreferences } from "../utils/referral";

const OPTIONS: { key: keyof Preferences; label: string; hint: string }[] = [
  { key: "signups", label: "A new account signs up with my code", hint: "We tell you the first name only." },
  { key: "earnings", label: "Earnings are added to my account", hint: "When we record a payment for an account you referred." },
  { key: "status", label: "My code or partner status changes", hint: "Your code is changed, or your account is paused or resumed." },
];

/** Checkboxes that save as soon as one is changed. Emails about your application and approval are always sent. */
const EmailPreferences: React.FC<{ user: User; initial?: Preferences }> = ({ user, initial }) => {
  const [prefs, setPrefs] = useState<Preferences>(initial ?? { status: true, signups: true, earnings: true });
  const [message, setMessage] = useState<string | null>(null);

  const change = async (key: keyof Preferences, value: boolean) => {
    const next = { ...prefs, [key]: value };
    setPrefs(next);
    setMessage(null);
    try {
      await saveEmailPreferences(user, next);
      setMessage("Saved.");
    } catch {
      setPrefs(prefs);
      setMessage("Could not save that. Please try again.");
    }
  };

  return (
    <section className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100" aria-labelledby="email-heading">
      <h2 id="email-heading" className="text-xl font-bold text-slate-900">Emails</h2>
      <p className="text-slate-600 text-sm mt-1">Choose which emails you get about your partner account.</p>
      <div className="mt-3 space-y-3">
        {OPTIONS.map((o) => (
          <label key={o.key} className="flex items-start gap-3">
            <input type="checkbox" className="mt-1 w-4 h-4" checked={prefs[o.key]} onChange={(e) => change(o.key, e.target.checked)} />
            <span>
              <span className="font-bold text-slate-900 block">{o.label}</span>
              <span className="text-slate-600 text-sm">{o.hint}</span>
            </span>
          </label>
        ))}
      </div>
      <p className="text-sm text-slate-600 mt-3" role="status">{message}</p>
    </section>
  );
};

export default EmailPreferences;
